/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import {
    object, shape, string, func, array, bool, arrayOf, number,
} from 'prop-types';
import { useQuery, useLazyQuery } from '@apollo/client';
import { useLocation } from 'react-router';
import { bindActionCreators } from 'redux';
import subscriptionConfig, { getSubscriptionOptions } from '../../../../helpers/common/subscription/subscription-helpers';
import findContent from '../../../../gql/queries/findContent';
import { getBrand } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { setSubscriptionDetail } from '../../../../../state/ducks/App/App-Actions';
import {
    capitalize, getIdByLabel, copyQueryBuilder, defaultSelectionQuery,
} from './Subscription/Helper';
import Subscription from './Subscription/Subscription';
import SubscriptionV2 from './SubscriptionV2/Subscription';
import { CrossMerch } from '../../CrossMerchContainer/CrossMerchContainer';
import { getActiveABTest } from '../../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import { brandByCode } from '../../../../pages/Checkout/containers/CheckoutFood/components/Partials/Helper/Helper';
import { offerCopyQueryBuilder } from './SubscriptionV2/Helper';

const SubscriptionContainer = ({
    subscriptionDetails,
    subscription,
    triggerSubscriptionCheck,
    brand,
    subscriptionsList, // redux data
    setSubscriptionDetailData,
    selectedSku,
    trackSubscriptionEvent,
    partNumber,
    subscriptionBannerType,
    isPdpRedesignEnabled,
    isPdpRedesignShortEnabled,
}) => {
    const { featureFlags = {} } = useContext(CrossMerch);
    const { pathname } = useLocation();
    /**
     * Feature enabled/disabled block
     */
    const enableNewSupBanner = featureFlags['enabled-new-pdp-sup-banner'] && subscriptionBannerType === 'variant';
    const selectedSkuSubscriptions = selectedSku?.subscriptionsList || {};
    const subscriptionIntervals = getSubscriptionOptions(selectedSkuSubscriptions.interval);
    const subscriptionDurations = getSubscriptionOptions(selectedSkuSubscriptions.duration);
    const isSubscriptionDurationListEnabled = featureFlags['is-subscription-duration'] && subscriptionDurations.length > 1;
    const isShowingFrequencyDropdown = featureFlags['is-subscription-frequency'] && subscriptionIntervals.length > 1;
    const isABTestingVariant = featureFlags['which-subscription-pdp-test-variant']?.variant || '';
    const [subscriptionCopy, setSubscriptionCopy] = useState({});
    const {
        isTypeDiscovery,
    } = subscription;

    const subscriptionType = isTypeDiscovery ? 'Discovery' : 'Basic';
    let defaultOptions = {};
    let defaultSubscriptionCopy = {};

    const skuSubscriptionType = capitalize(getSubscriptionOptions(selectedSkuSubscriptions.type)[0] || '') || subscriptionType;

    /**
     * getting Data for default selection of dropdown Code START
     */

    const csEntryTitle = `Default ${skuSubscriptionType?.toLowerCase?.() || ''}`;

    const findContentDefaultOptions = defaultSelectionQuery(csEntryTitle, brand);
    const defaultDropdownQuery = findContent(findContentDefaultOptions);
    const [getDefaultOptions, {
        data,
    }] = useLazyQuery(defaultDropdownQuery);

    if (data) {
        defaultOptions = {
            frequency: data?.findContent?.content?.entries?.[0]?.subscription_interval,
            duration: data?.findContent?.content?.entries?.[0]?.subscription_duration,
        };
        defaultSubscriptionCopy = data?.findContent?.content?.entries?.[0] || {};
    }

    // ------------------------- //

    /**
     * Setting default duration data.
     */
    const [durationValue, setDurationValue] = useState(getSubscriptionOptions(subscriptionsList?.duration)[0]  || subscriptionDurations[0]);
    const durationText = subscriptionConfig.subscription_duration?.[durationValue];

    /**
     * Setting Interval/Frequency both are same
     */

    const [frequencyValue, setFrequencyValue] = useState(getSubscriptionOptions(subscriptionsList?.interval)[0] || subscriptionIntervals[0]); // set default value
    const frequencyText = subscriptionConfig.subscription_interval?.[frequencyValue];
    /** *
     *
     *  getting copy from CS based on duration, interval and Type.
     *
     */
    const findContentOptions = copyQueryBuilder(durationText, frequencyText, skuSubscriptionType, brand);
    const subscriptionCopyQuery = findContent(findContentOptions);
    const { data: subscriptionCopyData } = useQuery(subscriptionCopyQuery);

    const findOfferCopy = offerCopyQueryBuilder(brandByCode?.[selectedSku?.brandId || 1001]);
    const subscriptionOfferCopyQuery = findContent(findOfferCopy);
    const pdpOfferResponse = useQuery(subscriptionOfferCopyQuery);
    const pdpOfferContent = pdpOfferResponse?.data?.findContent?.content?.entries?.[0] || {};
    const { data: globalSubscriptionCopyData, loading: globalSubscriptionCopyLoading } = useQuery(findContent({
        brand: 'global',
        contentType: 'subscriptions_global_copy',
    }));
    const globalSubscriptionCopy = globalSubscriptionCopyData?.findContent?.content?.entries?.[0] || {};
    useEffect(() => {
        setSubscriptionDetailData({});
        /**
         * getting copy from default entry
         */
        getDefaultOptions();
    }, []);

    useEffect(() => {
        const frequencyDefault = getIdByLabel(subscriptionConfig.subscription_interval, defaultOptions?.frequency);
        const durationDefault = getIdByLabel(subscriptionConfig.subscription_duration, defaultOptions?.duration);

        const availableIntervals = getSubscriptionOptions(selectedSku?.subscriptionsList?.interval);
        const availableDurations = getSubscriptionOptions(selectedSku?.subscriptionsList?.duration);
        const frequencyDefaultValue = availableIntervals.includes(frequencyDefault) ? frequencyDefault : (availableIntervals[0] || frequencyValue);
        const durationDefaultValue = availableDurations.includes(durationDefault) ? durationDefault : (availableDurations[0] || durationValue);

        // if value also available for sku
        setFrequencyValue(frequencyDefaultValue);
        setDurationValue(durationDefaultValue);

        // updating same option in Redux
        setSubscriptionDetailData({
            duration: durationDefaultValue || availableDurations[0] || subscriptionsList.duration,
            interval: frequencyDefaultValue || availableIntervals[0] || subscriptionsList.interval,
            subscriptionInterval: frequencyDefaultValue || availableIntervals[0] || subscriptionsList.subscriptionInterval,
            subscriptionType: skuSubscriptionType || subscriptionsList.subscriptionType,
        });
    }, [defaultOptions?.frequency, defaultOptions?.duration, selectedSku?.id]);

    useEffect(() => {
        setSubscriptionCopy(subscriptionCopyData?.findContent?.content?.entries?.[0] || {});
    }, [subscriptionCopyData]);

    useEffect(() => {
        // track event for subscription pdp banner ab test
        trackEvent({
            eventCategory: 'Test Impression',
            eventAction: 'Subscriptions PDP Modal Redesign',
            eventLabel: enableNewSupBanner ? 'variant' : 'control',
        });
    }, [pathname]);

    // Use the CMS-driven PDP module whenever the global copy contains it.
    const hasPdpModuleCopy = Boolean(globalSubscriptionCopy?.pdp_module?.heading);
    const SubscriptionComponent = hasPdpModuleCopy ? SubscriptionV2 : Subscription;

    if (globalSubscriptionCopyLoading) return null;

    // -------------  Rendering UI  -------------//
    return (
        <SubscriptionComponent
            subscriptionDetails={subscriptionDetails}
            subscription={subscription}
            triggerSubscriptionCheck={triggerSubscriptionCheck}
            brand={brand}
            subscriptionsList={subscriptionsList}
            setSubscriptionDetailData={setSubscriptionDetailData}
            featureFlags={featureFlags}
            selectedSku={selectedSku}
            trackSubscriptionEvent={trackSubscriptionEvent}
            pdpOfferContent={pdpOfferContent}
            globalSubscriptionCopy={globalSubscriptionCopy}
            isSubscriptionDurationListEnabled={isSubscriptionDurationListEnabled}
            isShowingFrequencyDropdown={isShowingFrequencyDropdown}
            isABTestingVariant={isABTestingVariant}
            durationActions={{
                durationValue,
                setDurationValue,
            }}
            frequencyActions={{
                frequencyValue,
                setFrequencyValue,
            }}

            subscriptionCopyActions={{
                subscriptionCopy: subscriptionCopy?.single_line_label ? subscriptionCopy : defaultSubscriptionCopy, // passing copy default entry
                setSubscriptionCopy,
            }}
            defaultOptions={defaultOptions}
            partNumber={partNumber}
            isPdpRedesignEnabled={isPdpRedesignEnabled}
            isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
        />
    );
};

SubscriptionContainer.propTypes = {
    subscriptionsList: shape({
        duration: arrayOf(
            number,
        ),
        type: arrayOf(
            string,
        ),
        interval: arrayOf(
            number,
        ),
    }),
    subscriptionDetails: shape({
        subscriptionType: string.isRequired,
        subscriptionDuration: string.isRequired,
        subscriptionFrequency: string.isRequired,
        subscriptionDurationKey: string.isRequired,
    }).isRequired,
    subscription: shape({
        subscriptionKey: array.isRequired,
        subscriptionIndefinite: string.isRequired,
        subscriptionType: array.isRequired,
        implementProductSubscription: bool.isRequired,
        viewSubscription: bool.isRequired,
        isTypeDiscovery: bool.isRequired,
        subscriptionCheckbox: bool.isRequired,
    }).isRequired,
    setSubscriptionDetailData: func.isRequired,
    triggerSubscriptionCheck: func.isRequired,
    brand: shape({
        domain: string,
    }).isRequired,

    selectedSku: object.isRequired,
    trackSubscriptionEvent: func.isRequired,
    partNumber: string.isRequired,
    subscriptionBannerType: string.isRequired,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
};

SubscriptionContainer.defaultProps = {
    subscriptionsList: {},
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
};

const mapDispatchToProps = (dispatch) => ({
    setSubscriptionDetailData: bindActionCreators(setSubscriptionDetail, dispatch),
    trackSubscriptionEvent: bindActionCreators(trackEvent, dispatch),
});

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    subscriptionsList: state.ui.subscriptionDetail,
    subscriptionBannerType: getActiveABTest('pdp_subsriptoin_banner')(state),
});

const enhance = compose(
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(SubscriptionContainer);
